import React, { useState } from 'react';
import ActivityTab from './ActivityTab';
import { useNavigate } from 'react-router-dom';
import PartnersSection from './PartnersSection';
import UsersSection from './UsersSection';
import TaskListsSection from './TaskListsSection';
import { Users, TrendingUp, AlertCircle, CheckCircle2, Clock, DollarSign, Settings } from 'lucide-react';
import { refreshAllSellerTaskDates } from '../services/transactionTasks';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');

  // Recalculates open task dates on every seller deal, so existing deals follow the current task logic.
  const runSellerRefresh = async () => {
    if (!window.confirm('Recalculate open task dates on every seller deal in the office?')) return;
    setRefreshing(true);
    setRefreshMsg('');
    try {
      const r = await refreshAllSellerTaskDates();
      setRefreshMsg(`Checked ${r.deals} seller deals and updated ${r.updated} task dates${r.failed ? `, ${r.failed} could not be updated` : ''}.`);
    } catch (err) {
      console.error('Seller task refresh failed', err);
      setRefreshMsg('The refresh did not finish. Check the browser console for details.');
    } finally {
      setRefreshing(false);
    }
  };

  const recentTransactions = [
    { id: 'TXN-2024-089', property: '456 Corkscrew Ln', agent: 'Kelly Olin', status: 'closing', daysLeft: 2 },
    { id: 'TXN-2024-088', property: '123 Verdana Way', agent: 'Amanda Bolduc', status: 'inspection', daysLeft: 5 },
    { id: 'TXN-2024-087', property: '789 Kingston Dr', agent: 'Carrie Cabrera', status: 'undercontract', daysLeft: 12 },
    { id: 'TXN-2024-086', property: '321 Estero Blvd', agent: 'Liz Harris', status: 'pending', daysLeft: 3 },
  ];

  const systemAlerts = [
    { id: 1, type: 'warning', message: 'TXN-2024-089 closing in 2 days - final walkthrough needed' },
    { id: 2, type: 'info', message: 'New user Briggs Olin pending approval in admin dashboard' },
    { id: 3, type: 'success', message: 'TXN-2024-085 closed successfully - funds transferred' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      closing: 'bg-[#f5efe1] border-[#d8c9a8] text-[#1a1a1a]',
      inspection: 'bg-[#f7f7f7] border-[#e0e0e0] text-[#1a1a1a]',
      undercontract: 'bg-white border-[#D4AF37] text-[#1a1a1a]',
      pending: 'bg-white border-[#e0e0e0] text-[#1a1a1a]',
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      closing: 'Closing',
      inspection: 'Inspection',
      undercontract: 'Under Contract',
      pending: 'Pending',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6 admin-header">
            <div>
              <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2">Admin Dashboard</h1>
              <p className="text-[#6b6b6b]">RESIDENCE | eXp Realty Team Management</p>
              {refreshMsg && <p className="text-sm text-[#1a1a1a] mt-2" role="status">{refreshMsg}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={runSellerRefresh} disabled={refreshing} className="flex items-center gap-2 bg-[#1a1a1a] text-[#D4AF37] px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-shadow disabled:opacity-60">
                <span className="font-medium">{refreshing ? 'Refreshing seller deals...' : 'Refresh seller task dates'}</span>
              </button>
              <button className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md border border-[#e5e5e5] transition-shadow">
              <Settings className="w-5 h-5 text-[#6b6b6b]" />
              <span className="text-[#1a1a1a] font-medium">Settings</span>
            </button>
            </div>
          </div>

        </div>


        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#e5e5e5] admin-tabs">
          {['overview', 'users', 'partners', 'tasklists', 'transactions', 'alerts', 'activity'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-medium transition-colors ${
                activeTab === tab
                  ? 'text-[#1a1a1a] border-b-2 border-[#D4AF37]'
                  : 'text-[#6b6b6b] hover:text-[#1a1a1a]'
              }`}
            >
              {tab === 'tasklists' ? 'Task Lists' : tab === 'activity' ? 'Activity' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-[#1a1a1a] mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/commissions')}
                  className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  View Commissions
                </button>
                <button onClick={() => navigate('/transactions/new')} className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  Add Transaction
                </button>
                <button className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  Invite Agent
                </button>
                <button className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  View Reports
                </button>
                <button
                  onClick={() => navigate('/admin/agent-settings')}
                  className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  Agent Settings
                </button>
                <button
                  onClick={() => navigate('/admin/team-lead-certification')}
                  className="w-full bg-[#f5efe1] hover:bg-[#ebe2cc] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  Team Lead Certification
                </button>
                <button className="w-full bg-[#f3f1ec] hover:bg-[#ebe7de] text-[#1a1a1a] font-medium py-2 px-4 rounded-lg transition-colors">
                  Team Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'users' && (
          <UsersSection />
        )}

        {activeTab === 'partners' && (
          <div>
            <PartnersSection />
          </div>
        )}

        {activeTab === 'tasklists' && (
          <TaskListsSection />
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-6">Recent Transactions</h2>
            <div className="space-y-3">
              {recentTransactions.map(txn => (
                <div key={txn.id} className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(txn.status)}`}>
                  <div className="flex-1">
                    <p className="font-medium">{txn.property}</p>
                    <p className="text-sm opacity-75">{txn.agent}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium mb-1">{getStatusLabel(txn.status)}</p>
                    <p className="text-xs opacity-75">{txn.daysLeft} days remaining</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-6">System Alerts</h2>
            <div className="space-y-3">
              {systemAlerts.map(alert => {
                const icons = {
                  warning: <AlertCircle className="w-5 h-5 text-[#8C6D1F]" />,
                  info: <Clock className="w-5 h-5 text-[#1a1a1a]" />,
                  success: <CheckCircle2 className="w-5 h-5 text-[#1a1a1a]" />,
                };
                const bgColors = {
                  warning: 'bg-[#f5efe1] border-[#d8c9a8]',
                  info: 'bg-[#f7f7f7] border-[#e0e0e0]',
                  success: 'bg-white border-[#1a1a1a]',
                };
                return (
                  <div key={alert.id} className={`flex items-start gap-3 p-4 rounded-lg border ${bgColors[alert.type]}`}>
                    {icons[alert.type]}
                    <div className="flex-1">
                      <p className="text-[#1a1a1a] font-medium">{alert.message}</p>
                    </div>
                    <button className="text-[#6b6b6b] hover:text-[#1a1a1a] text-sm font-medium">Dismiss</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;